import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {  useNavigate } from "@tanstack/react-router";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@/lib/axios";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

const inputStyles =
  "rounded-lg border-neutral-200 focus:border-neutral-500 focus:ring-neutral-500";

const formSchema = z.object({
    password: z.string().min(8, {
        message: "Password should be longer than 8 character",
      }),
      confirmPassword: z.string().min(8, {
        message: "Password should be longer than 8 character",
      })  
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })

export function ResetPasswordForm({token}: {token: string}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const navigate = useNavigate();
  const { mutateAsync: resetPassword, isPending } = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const response = await api.post("/api/auth/reset-password", {
        newPassword: data.password,
        token: token,
      });
      return response.data;
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.message);
      } else {
        toast.error("Something went wrong");
      }
    },
    onSuccess: () => {
      toast.success("Password reset successfully");
      navigate({ to: "/login" });
    },
  });


  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    await resetPassword(data);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 w-full"
        noValidate
        aria-label="Registration form"
      >
        <div className="grid gap-4 sm:grid-cols-1 w-full">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    placeholder="New password"
                    type="password"
                    autoComplete="new-password"
                    className={inputStyles}
                    aria-required="true"
                    aria-label="Password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    placeholder="Confirm your new password"
                    type="password"
                    autoComplete="new-password"
                    className={inputStyles}
                    aria-required="true"
                    aria-label="Confirm password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          className="w-full bg-neutral-950 text-white transition-all hover:bg-neutral-800"
          type="submit"
          aria-busy={isPending}
          disabled={isPending}
        >
          {isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          Reset Password
        </Button>
      </form>
    </Form>
  );
}
